const TOKEN_STORAGE_KEY = 'wt_auth_token';

/**
 * Extracts an SSO token from URL search parameters (?session=... or ?token=...)
 * or from hash query parameters (#/path?session=... or #/?session=...), persists it
 * to localStorage, and strips the token from the URL to prevent leakage and
 * infinite redirect loops.
 */
export function captureAndPersistSsoToken(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const searchParams = new URLSearchParams(window.location.search);
    let token = searchParams.get('session') || searchParams.get('token');

    // Also inspect hash fragment for query parameters
    if (!token && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.split('?')[1] || '';
      const hashParams = new URLSearchParams(hashQuery);
      token = hashParams.get('session') || hashParams.get('token');
    }

    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);

      // Clean search parameters if token was present in search
      if (searchParams.has('session') || searchParams.has('token')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('session');
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.toString());
      }

      // Clean hash parameters if token was present in hash
      if (window.location.hash.includes('session=') || window.location.hash.includes('token=')) {
        const hashParts = window.location.hash.split('?');
        const hashBase = hashParts[0];
        const hashQuery = hashParts[1] || '';
        const hp = new URLSearchParams(hashQuery);
        hp.delete('session');
        hp.delete('token');
        const remaining = hp.toString();
        window.location.hash = remaining ? `${hashBase}?${remaining}` : hashBase;
      }

      return token;
    }
  } catch (err) {
    console.warn('[auth] Could not capture SSO token:', err);
  }

  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Returns a clean redirect URL for authentication gates without stale session
 * or token query parameters.
 */
export function getCleanRedirectUrl(): string {
  if (typeof window === 'undefined') return '';
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('session');
    url.searchParams.delete('token');

    // Clean hash query params if any
    if (url.hash.includes('session=') || url.hash.includes('token=')) {
      const hashParts = url.hash.split('?');
      const hashBase = hashParts[0];
      const hashQuery = hashParts[1] || '';
      const hp = new URLSearchParams(hashQuery);
      hp.delete('session');
      hp.delete('token');
      const remaining = hp.toString();
      url.hash = remaining ? `${hashBase}?${remaining}` : hashBase;
    }

    return url.toString();
  } catch {
    return window.location.origin + window.location.pathname;
  }
}
